'use client';
import React, { FC, useState } from 'react';
import { Button, LuvImage } from '@/components/ui';
import { BUTTON_SIZES, BUTTON_STYLES } from '@/components/ui/Buttons/utils';
import { Entry } from 'contentful';
import { TypeProductFields } from '@/core/generated-types';
import classNames from 'classnames';
import {
  InventoryStatusCode,
  ShopifyProduct,
  getProductInventoryStatus,
} from '@/lib/shopify';
import LookInsideModal from '../LookInsideModal/LookInsideModal';
import ProductVariantModal from './ProductVariantModal';
import { SelectedAddOns } from '../ProductAddToCart/ProductAddToCart';
import useInventoryStatus from '@/hooks/useInventoryStatus';
import { InventoryStatusModal } from '../InventoryStatus';
import { getDefaultVariant } from '@/utils/shopify';

interface ProductCtaButtonProps {
  isSelected: boolean;
  shopifyProduct: ShopifyProduct | null;
  handleClick: () => void;
}

const ProductCtaButton = ({
  isSelected,
  shopifyProduct,
  handleClick,
}: ProductCtaButtonProps) => {
  const [isInventoryStatusModalOpen, setIsInventoryStatusModalOpen] =
    useState(false);
  const inventoryStatus = getProductInventoryStatus(shopifyProduct);
  const isProductOutOfStock =
    inventoryStatus === InventoryStatusCode.OUT_OF_STOCK;
  const [ctaText, ctaClasses, isCtaDisabled, modalTitle, modalDescription] =
    useInventoryStatus(shopifyProduct, false, true);
  const productCtaText = isSelected
    ? '- Remove'
    : isProductOutOfStock
      ? ctaText
      : '+ Add';

  return (
    <>
      <Button
        ctaText={productCtaText}
        disabled={isCtaDisabled}
        onClick={() => {
          isProductOutOfStock
            ? setIsInventoryStatusModalOpen(true)
            : handleClick();
        }}
        style={BUTTON_STYLES.secondary}
        classes={classNames('ml-auto px-4', ctaClasses)}
      />

      {isProductOutOfStock && (
        <InventoryStatusModal
          isOpen={isInventoryStatusModalOpen}
          handleClose={() => setIsInventoryStatusModalOpen(false)}
          ctaText={'keep shopping'}
          title={modalTitle}
          description={modalDescription}
        />
      )}
    </>
  );
};

interface ProductAddOnProps {
  productAddOn: Entry<TypeProductFields>;
  shopifyProductAddOn: ShopifyProduct | null;
  selectedAddOns: SelectedAddOns[];
  setSelectedAddOns: (selectedAddOns: SelectedAddOns[]) => void;
}

const ProductAddOn: FC<ProductAddOnProps> = ({
  productAddOn,
  shopifyProductAddOn,
  selectedAddOns,
  setSelectedAddOns,
}) => {
  const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
  const [isProductDetailsModalOpen, setIsProductDetailsModalOpen] =
    useState(false);
  const isOrganicCottonBabyDoll =
    productAddOn.fields.title === 'Organic Cotton Baby Doll';
  const [isSelected, setIsSelected] = useState(false);
  const {
    fields: { title, featuredImage },
  } = productAddOn;
  const defaultVariant = getDefaultVariant(
    productAddOn.fields.title,
    (shopifyProductAddOn as ShopifyProduct).variants,
  );
  const { formattedPrice } = defaultVariant;
  const [selectedVariant, setSelectedVariant] = useState(defaultVariant);

  const addProduct = () => {
    setIsSelected(true);
    const addOnWithFieldsAndVariant = {
      fields: productAddOn.fields,
      variant: selectedVariant,
    };
    setSelectedAddOns([...selectedAddOns, addOnWithFieldsAndVariant]);
  };

  const removeProduct = () => {
    setIsSelected(false);
    setSelectedAddOns(
      selectedAddOns.filter(
        (addOn) => addOn.fields.entryName !== productAddOn.fields.entryName,
      ),
    );
  };

  const addProductVariant = () => {
    setIsSelected(true);
    setIsVariantModalOpen(false);
    const addOnWithFieldsAndVariant = {
      fields: productAddOn.fields,
      variant: selectedVariant,
    };
    setSelectedAddOns([...selectedAddOns, addOnWithFieldsAndVariant]);
  };

  const handleOrganicCottonBabyDollClick = () =>
    isOrganicCottonBabyDoll ? setIsVariantModalOpen(true) : addProduct();

  const handleClick = () =>
    isSelected ? removeProduct() : handleOrganicCottonBabyDollClick();

  return (
    <li className="mb-4 flex items-center">
      <div
        className={classNames(
          'align-center flex h-[86px] w-[86px] flex-shrink-0 border-[3px] border-solid bg-khaki-tint',
          isSelected ? 'border-indigo' : 'border-transparent',
        )}
      >
        <LuvImage
          imageData={featuredImage}
          height={80}
          width={80}
          alt={featuredImage.fields.altText}
          className="object-contain"
        />
      </div>
      <div className="flex flex-col text-base">
        <Button
          ctaText={title}
          onClick={() => {
            setIsProductDetailsModalOpen(true);
          }}
          size={BUTTON_SIZES.md}
          style={BUTTON_STYLES.link}
          classes="text-left text-base h-auto min-h-fit px-3"
        />
        <span className="px-3">{formattedPrice}</span>
      </div>

      <ProductCtaButton
        isSelected={isSelected}
        shopifyProduct={shopifyProductAddOn}
        handleClick={handleClick}
      />

      <LookInsideModal
        isOpen={isProductDetailsModalOpen}
        handleClose={() => setIsProductDetailsModalOpen(false)}
        product={productAddOn}
        addProduct={addProduct}
        removeProduct={removeProduct}
        isSelected={isSelected}
        shopifyProduct={shopifyProductAddOn}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
      />

      {isOrganicCottonBabyDoll && (
        <ProductVariantModal
          isOpen={isVariantModalOpen}
          handleClose={() => setIsVariantModalOpen(false)}
          product={productAddOn}
          addProduct={addProductVariant}
          shopifyProductAddOn={shopifyProductAddOn}
          selectedVariant={selectedVariant}
          setSelectedVariant={setSelectedVariant}
        />
      )}
    </li>
  );
};

export default React.memo(ProductAddOn);
